import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling", data: null });

  useEffect(() => {
    if (!sessionId) { setStatus({ state: "error", data: null }); return; }
    let attempts = 0;
    const maxAttempts = 8; // ~16s
    const poll = async () => {
      attempts++;
      try {
        const r = await http.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus({ state: "paid", data: r.data });
          refresh?.();
          return;
        }
      } catch (e) { console.debug("payment status poll retry", e?.message); }
      if (attempts >= maxAttempts) {
        setStatus({ state: "timeout", data: null });
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- attempts/maxAttempts/poll are closure-scoped inside the effect; only session change should re-run.
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="payment-success-page">
        {status.state === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-slate-400 animate-spin mx-auto mb-4" />
            <div className="font-display text-2xl font-bold">Confirming payment…</div>
            <p className="text-slate-500 mt-2 text-sm">Just a moment while Stripe finalises your subscription.</p>
          </>
        )}
        {status.state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="font-display text-3xl font-extrabold">You're in!</div>
            <p className="text-slate-600 mt-2">Your <strong>{status.data?.plan || "plan"}</strong> subscription is active with a 7-day free trial.</p>
            <Button onClick={() => nav("/learners")} className="rounded-full mt-6 h-11 px-6" data-testid="payment-success-continue">
              Go to app
            </Button>
          </>
        )}
        {status.state === "timeout" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <div className="font-display text-2xl font-bold">Still processing…</div>
            <p className="text-slate-500 mt-2 text-sm">Stripe is taking longer than expected. Your subscription should appear shortly — check the Billing page in a minute.</p>
            <Button onClick={() => nav("/billing")} className="rounded-full mt-6" variant="outline">Open Billing</Button>
          </>
        )}
        {status.state === "error" && (
          <>
            <XCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
            <div className="font-display text-2xl font-bold">Something went wrong</div>
            <p className="text-slate-500 mt-2 text-sm">We couldn't find that payment session. Please try again or contact support.</p>
            <Button onClick={() => nav("/billing")} className="rounded-full mt-6">Try again</Button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="payment-cancel-page">
        <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <div className="font-display text-2xl font-bold">Checkout cancelled</div>
        <p className="text-slate-500 mt-2 text-sm">No worries — you can restart the trial any time from Billing.</p>
        <Button onClick={() => nav("/billing")} className="rounded-full mt-6" data-testid="payment-cancel-retry">
          Back to plans
        </Button>
      </div>
    </div>
  );
}
